import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, Edit, Trash2, Eye, FileText, Users, Mail, 
  MessageSquare, BarChart3, Loader2, Save, X, Upload
} from "lucide-react";
import { format } from "date-fns";

const MediaUploader = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [copied, setCopied] = useState(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const resp = await axios.post(`${API}/upload/video`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      const serveUrl = `${window.location.origin}${resp.data.serve_url}`;
      setUploadedVideos(prev => [...prev, { ...resp.data, full_url: serveUrl }]);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyUrl = (url, idx) => {
    navigator.clipboard.writeText(url);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
    toast.success("URL copied to clipboard");
  };

  return (
    <div>
      <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">Upload Videos</h2>
      <p className="text-muted-foreground text-sm mb-6">Upload full-quality video files here. Copy the URL and paste it into an article's reel videos or content.</p>
      
      <label className="relative cursor-pointer block border-2 border-dashed border-border hover:border-primary/50 transition-colors p-8 text-center mb-8" data-testid="video-upload-area">
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={handleVideoUpload}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Click to upload video</span>
            <span className="font-mono text-xs text-muted-foreground/50">MP4, MOV, WebM</span>
          </div>
        )}
      </label>

      {uploadedVideos.length > 0 && (
        <div className="space-y-6">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Uploaded This Session</h3>
          {uploadedVideos.map((vid, idx) => (
            <div key={idx} className="border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-foreground truncate max-w-[60%]">{vid.filename}</span>
                <span className="font-mono text-xs text-muted-foreground">{(vid.size / 1024 / 1024).toFixed(1)}MB</span>
              </div>
              <video src={vid.full_url} controls playsInline preload="metadata" className="w-full max-h-[300px] bg-black" />
              <div className="flex gap-2">
                <Input value={vid.full_url} readOnly className="bg-background border-border font-mono text-xs flex-1" />
                <Button 
                  onClick={() => copyUrl(vid.full_url, idx)} 
                  variant="outline" 
                  className="border-border font-mono text-xs uppercase shrink-0"
                  data-testid={`copy-video-url-${idx}`}
                >
                  {copied === idx ? "Copied!" : "Copy URL"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const { user, login } = useAuth();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Article form state
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    category_id: "",
    author_id: "",
    tags: "",
    is_featured: false,
    status: "draft",
    youtube_embed: "",
    tiktok_embed: "",
    instagram_embeds: "",
    photo_gallery: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, articlesRes, authorsRes, categoriesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/articles?status=published&limit=100`),
        axios.get(`${API}/authors`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/subscribers`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setArticles(articlesRes.data.articles);
      setAuthors(authorsRes.data);
      setCategories(categoriesRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleArticleFormChange = (field, value) => {
    setArticleForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'title' && !editingArticle) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt || "",
        content: article.content || "",
        featured_image: article.featured_image || "",
        category_id: article.category_id,
        author_id: article.author_id,
        tags: article.tags?.join(", ") || "",
        is_featured: article.is_featured || false,
        status: article.status || "draft",
        youtube_embed: article.youtube_embed || "",
        tiktok_embed: article.tiktok_embed || "",
        instagram_embeds: article.instagram_embeds?.join("\n") || "",
        photo_gallery: article.photo_gallery || []
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        featured_image: "",
        category_id: categories[0]?.category_id || "",
        author_id: authors[0]?.author_id || "",
        tags: "",
        is_featured: false,
        status: "draft",
        youtube_embed: "",
        tiktok_embed: "",
        instagram_embeds: "",
        photo_gallery: []
      });
    }
    setArticleDialogOpen(true);
  };

  const saveArticle = async () => {
    if (!articleForm.title || !articleForm.category_id || !articleForm.author_id) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...articleForm,
        tags: articleForm.tags.split(",").map(t => t.trim()).filter(Boolean),
        youtube_embed: articleForm.youtube_embed?.trim() || null,
        tiktok_embed: articleForm.tiktok_embed?.trim() || null,
        instagram_embeds: articleForm.instagram_embeds?.trim() 
          ? articleForm.instagram_embeds.split("\n").map(u => u.trim()).filter(Boolean) 
          : null,
        photo_gallery: articleForm.photo_gallery && articleForm.photo_gallery.length > 0
          ? articleForm.photo_gallery
          : null
      };

      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.article_id}`, payload, { withCredentials: true });
        toast.success("Article updated!");
      } else {
        await axios.post(`${API}/articles`, payload, { withCredentials: true });
        toast.success("Article created!");
      }
      
      setArticleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save article");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      await axios.delete(`${API}/articles/${articleId}`, { withCredentials: true });
      toast.success("Article deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen" data-testid="admin-page">
      {/* Header */}
      <section className="py-8 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Admin</span>
              <h1 className="font-heading font-black text-3xl uppercase tracking-tighter mt-1">
                Dashboard
              </h1>
            </div>
            <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => openArticleDialog()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 font-mono uppercase tracking-widest text-xs"
                  data-testid="admin-new-article"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Article
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-xl uppercase tracking-tight">
                    {editingArticle ? "Edit Article" : "New Article"}
                  </DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    {editingArticle ? "Update the article details below." : "Create a new article for the magazine."}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Title *</label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => handleArticleFormChange('title', e.target.value)}
                      placeholder="Article title"
                      className="bg-background border-border"
                      data-testid="article-form-title"
                    />
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Slug</label>
                    <Input
                      value={articleForm.slug}
                      onChange={(e) => handleArticleFormChange('slug', e.target.value)}
                      placeholder="article-url-slug"
                      className="bg-background border-border font-mono text-sm"
                      data-testid="article-form-slug"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Category *</label>
                      <Select 
                        value={articleForm.category_id} 
                        onValueChange={(v) => handleArticleFormChange('category_id', v)}
                      >
                        <SelectTrigger className="bg-background border-border" data-testid="article-form-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {categories.map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Author *</label>
                      <Select 
                        value={articleForm.author_id} 
                        onValueChange={(v) => handleArticleFormChange('author_id', v)}
                      >
                        <SelectTrigger className="bg-background border-border" data-testid="article-form-author">
                          <SelectValue placeholder="Select author" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {authors.map(author => (
                            <SelectItem key={author.author_id} value={author.author_id}>{author.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Excerpt</label>
                    <Textarea
                      value={articleForm.excerpt}
                      onChange={(e) => handleArticleFormChange('excerpt', e.target.value)}
                      placeholder="Brief description of the article"
                      className="bg-background border-border resize-none h-20"
                      data-testid="article-form-excerpt"
                    />
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Content</label>
                    <Textarea
                      value={articleForm.content}
                      onChange={(e) => handleArticleFormChange('content', e.target.value)}
                      placeholder="Full article content..."
                      className="bg-background border-border resize-none h-48"
                      data-testid="article-form-content"
                    />
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Featured Image URL</label>
                    <Input
                      value={articleForm.featured_image}
                      onChange={(e) => handleArticleFormChange('featured_image', e.target.value)}
                      placeholder="https://..."
                      className="bg-background border-border"
                      data-testid="article-form-image"
                    />
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Tags (comma separated)</label>
                    <Input
                      value={articleForm.tags}
                      onChange={(e) => handleArticleFormChange('tags', e.target.value)}
                      placeholder="music, culture, manchester"
                      className="bg-background border-border"
                      data-testid="article-form-tags"
                    />
                  </div>
                  
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">YouTube Embed URL</label>
                    <Input
                      value={articleForm.youtube_embed}
                      onChange={(e) => handleArticleFormChange('youtube_embed', e.target.value)}
                      placeholder="https://www.youtube.com/embed/VIDEO_ID"
                      className="bg-background border-border"
                      data-testid="article-form-youtube"
                    />
                    <p className="font-mono text-xs text-muted-foreground/60 mt-1">Use embed format: youtube.com/embed/VIDEO_ID</p>
                  </div>

                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">TikTok URL</label>
                    <Input
                      value={articleForm.tiktok_embed}
                      onChange={(e) => handleArticleFormChange('tiktok_embed', e.target.value)}
                      placeholder="https://www.tiktok.com/@username/video/VIDEO_ID"
                      className="bg-background border-border"
                      data-testid="article-form-tiktok"
                    />
                  </div>

                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Instagram Reels (one URL per line)</label>
                    <Textarea
                      value={articleForm.instagram_embeds}
                      onChange={(e) => handleArticleFormChange('instagram_embeds', e.target.value)}
                      placeholder={"https://www.instagram.com/reel/ABC123/\nhttps://www.instagram.com/reel/DEF456/"}
                      className="bg-background border-border resize-none h-20"
                      data-testid="article-form-instagram"
                    />
                    <p className="font-mono text-xs text-muted-foreground/60 mt-1">Paste Instagram reel URLs, one per line</p>
                  </div>

                  {/* Photo Gallery Uploader */}
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                      Photo Gallery ({(articleForm.photo_gallery || []).length} photos)
                    </label>
                    <div className="border border-border p-4 space-y-3 bg-background/50">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={saving}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setSaving(true);
                          try {
                            const formData = new FormData();
                            files.forEach((f) => formData.append("files", f));
                            const resp = await axios.post(`${API}/upload/images`, formData, {
                              headers: { "Content-Type": "multipart/form-data" },
                              withCredentials: true,
                            });
                            const backend = process.env.REACT_APP_BACKEND_URL;
                            const urls = (resp.data.uploaded || []).map((u) =>
                              u.serve_url.startsWith("http") ? u.serve_url : `${backend}${u.serve_url}`
                            );
                            setArticleForm((prev) => ({
                              ...prev,
                              photo_gallery: [...(prev.photo_gallery || []), ...urls],
                            }));
                            toast.success(`Uploaded ${urls.length} photo${urls.length === 1 ? "" : "s"}`);
                          } catch (err) {
                            toast.error("Upload failed");
                            console.error(err);
                          } finally {
                            setSaving(false);
                            e.target.value = "";
                          }
                        }}
                        className="font-mono text-xs w-full"
                        data-testid="gallery-upload-input"
                      />
                      <p className="font-mono text-xs text-muted-foreground/60">
                        Select multiple images at once (Cmd/Ctrl + click). JPG, PNG, WebP or GIF.
                      </p>

                      {(articleForm.photo_gallery || []).length > 0 && (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-2">
                          {articleForm.photo_gallery.map((url, i) => (
                            <div key={`${url}-${i}`} className="relative group aspect-square overflow-hidden border border-border">
                              <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() =>
                                  setArticleForm((prev) => ({
                                    ...prev,
                                    photo_gallery: prev.photo_gallery.filter((_, idx) => idx !== i),
                                  }))
                                }
                                className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white font-mono text-[10px] uppercase tracking-widest transition-opacity"
                                data-testid={`gallery-remove-${i}`}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Status</label>
                      <Select 
                        value={articleForm.status} 
                        onValueChange={(v) => handleArticleFormChange('status', v)}
                      >
                        <SelectTrigger className="bg-background border-border" data-testid="article-form-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <input
                        type="checkbox"
                        id="is_featured"
                        checked={articleForm.is_featured}
                        onChange={(e) => handleArticleFormChange('is_featured', e.target.checked)}
                        className="h-4 w-4"
                        data-testid="article-form-featured"
                      />
                      <label htmlFor="is_featured" className="font-mono text-sm text-muted-foreground">Featured Article</label>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-4 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setArticleDialogOpen(false)}
                      className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-6 font-mono uppercase tracking-widest text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={saveArticle}
                      disabled={saving}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 font-mono uppercase tracking-widest text-xs"
                      data-testid="article-form-save"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Save</>}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-card border border-border mb-8">
              <TabsTrigger value="dashboard" className="font-mono text-xs uppercase tracking-wider" data-testid="admin-tab-dashboard">
                <BarChart3 className="h-4 w-4 mr-2" />Dashboard
              </TabsTrigger>
              <TabsTrigger value="articles" className="font-mono text-xs uppercase tracking-wider" data-testid="admin-tab-articles">
                <FileText className="h-4 w-4 mr-2" />Articles
              </TabsTrigger>
              <TabsTrigger value="authors" className="font-mono text-xs uppercase tracking-wider" data-testid="admin-tab-authors">
                <Users className="h-4 w-4 mr-2" />Authors
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="font-mono text-xs uppercase tracking-wider" data-testid="admin-tab-subscribers">
                <Mail className="h-4 w-4 mr-2" />Subscribers
              </TabsTrigger>
              <TabsTrigger value="media" className="font-mono text-xs uppercase tracking-wider" data-testid="admin-tab-media">
                <Upload className="h-4 w-4 mr-2" />Media
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="border border-border p-6">
                    <FileText className="h-6 w-6 text-primary mb-2" />
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Published</p>
                    <p className="font-heading font-black text-4xl" data-testid="stat-published">{stats.published_articles}</p>
                  </div>
                  <div className="border border-border p-6">
                    <Eye className="h-6 w-6 text-primary mb-2" />
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Total Views</p>
                    <p className="font-heading font-black text-4xl" data-testid="stat-views">{stats.total_views?.toLocaleString()}</p>
                  </div>
                  <div className="border border-border p-6">
                    <Users className="h-6 w-6 text-primary mb-2" />
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Authors</p>
                    <p className="font-heading font-black text-4xl" data-testid="stat-authors">{stats.total_authors}</p>
                  </div>
                  <div className="border border-border p-6">
                    <Mail className="h-6 w-6 text-primary mb-2" />
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Subscribers</p>
                    <p className="font-heading font-black text-4xl" data-testid="stat-subscribers">{stats.total_subscribers}</p>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <div className="border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-card">
                      <tr className="border-b border-border">
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Title</th>
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Author</th>
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Views</th>
                        <th className="text-right p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map((article) => (
                        <tr key={article.article_id} className="border-b border-border/50 hover:bg-card/50" data-testid={`article-row-${article.article_id}`}>
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{article.title}</p>
                              <p className="font-mono text-xs text-muted-foreground mt-1">{article.slug}</p>
                            </div>
                          </td>
                          <td className="p-4 font-mono text-sm text-muted-foreground">
                            {authors.find(a => a.author_id === article.author_id)?.name || "-"}
                          </td>
                          <td className="p-4">
                            <span className={`font-mono text-xs uppercase tracking-wider px-2 py-1 ${
                              article.status === 'published' ? 'text-green-400 bg-green-400/10' : 'text-yellow-400 bg-yellow-400/10'
                            }`}>
                              {article.status}
                            </span>
                          </td>
                          <td className="p-4 font-mono text-sm">{article.views?.toLocaleString() || 0}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openArticleDialog(article)}
                                className="h-8 w-8 hover:text-primary"
                                data-testid={`edit-article-${article.article_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteArticle(article.article_id)}
                                className="h-8 w-8 hover:text-destructive"
                                data-testid={`delete-article-${article.article_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Authors Tab */}
            <TabsContent value="authors">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {authors.map((author) => (
                  <div key={author.author_id} className="border border-border p-6" data-testid={`author-card-admin-${author.author_id}`}>
                    <div className="flex items-center gap-4">
                      {author.avatar ? (
                        <img src={author.avatar} alt={author.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                          <Users className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{author.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{author.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Subscribers Tab */}
            <TabsContent value="subscribers">
              <div className="border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-card">
                      <tr className="border-b border-border">
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Email</th>
                        <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="p-8 text-center text-muted-foreground">No subscribers yet</td>
                        </tr>
                      ) : (
                        subscribers.map((sub) => (
                          <tr key={sub.subscriber_id} className="border-b border-border/50 hover:bg-card/50">
                            <td className="p-4 font-mono text-sm">{sub.email}</td>
                            <td className="p-4 font-mono text-xs text-muted-foreground">
                              {sub.subscribed_at ? format(new Date(sub.subscribed_at), "MMM dd, yyyy") : "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media">
              <MediaUploader />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
